import { TaskGenerationJobRunDetailsPage } from '@/app/jobs/TaskGenerationJobRunDetailsPage';
import { JobStatusBadge } from '@/components/badges/JobStatusBadge';
import { useRowClick } from '@/lib/navigation';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { taskGenerationJobRunsState } from '@/state/taskGenerationJobRuns.state';
import { useLoadableAtomValue } from '@/state/loadableHooks';
import logger from '@/util/logger';
import { TaskGenerationJobRunDao } from '@rock/db-admin';
import type { TaskGenerationJobRun } from '@rock/types';
import type { FC } from 'react';
import { useState } from 'react';
import { Route, Routes } from 'react-router-dom';
import { toast } from 'sonner';

const log = logger('TaskGenerationJobsPage');
const taskGenerationJobRunDao = new TaskGenerationJobRunDao();

export const TaskGenerationJobsPage: FC = () => {
    return (
        <Routes>
            <Route index element={<TaskGenerationJobsList />} />
            <Route path=":jobId" element={<TaskGenerationJobRunDetailsPage />} />
        </Routes>
    );
};

const TaskGenerationJobsList: FC = () => {
    const [runs, runsLoadable] = useLoadableAtomValue(taskGenerationJobRunsState, []);
    const [isExecuting, setIsExecuting] = useState(false);
    const isLoading = runsLoadable.state === 'loading';
    const hasError = runsLoadable.state === 'hasError';

    const handleExecuteNow = async () => {
        setIsExecuting(true);
        try {
            const runId = await taskGenerationJobRunDao.scheduleTaskGenerationNow();
            toast.success('Task generation scheduled', {
                description: `Job run ${runId} was created. Tasks will be generated shortly.`,
            });
            log.info('Scheduled task-generation job run from admin', { runId });
        } catch (err) {
            log.error('Error scheduling task-generation job run', { err });
            toast.error('Error scheduling task generation', {
                description: err instanceof Error ? err.message : 'An unexpected error occurred.',
            });
        } finally {
            setIsExecuting(false);
        }
    };

    if (isLoading) {
        return (
            <Card>
                <CardHeader>
                    <CardTitle>Loading job runs...</CardTitle>
                    <CardDescription>Please wait while we fetch task generation job runs.</CardDescription>
                </CardHeader>
            </Card>
        );
    }
    if (hasError) {
        return (
            <Card>
                <CardHeader>
                    <CardTitle>Error loading job runs</CardTitle>
                    <CardDescription>Unable to load job runs. Please try again later.</CardDescription>
                </CardHeader>
            </Card>
        );
    }
    if (runs.length === 0) {
        return (
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <div>
                            <CardTitle>No job runs found</CardTitle>
                            <CardDescription>No task generation job runs have been created yet.</CardDescription>
                        </div>
                        <Button onClick={handleExecuteNow} disabled={isExecuting}>
                            {isExecuting ? 'Scheduling…' : 'Execute Now'}
                        </Button>
                    </div>
                </CardHeader>
            </Card>
        );
    }
    return (
        <Card>
            <CardHeader>
                <div className="flex items-center justify-between">
                    <div>
                        <CardTitle>Recent Task Generation Job Runs</CardTitle>
                        <CardDescription>
                            Showing the {runs.length} most recent task generation job runs.
                        </CardDescription>
                    </div>
                    <Button onClick={handleExecuteNow} disabled={isExecuting}>
                        {isExecuting ? 'Scheduling…' : 'Execute Now'}
                    </Button>
                </div>
            </CardHeader>
            <CardContent>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Run ID</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Requested At</TableHead>
                            <TableHead>Finished At</TableHead>
                            <TableHead>Subscriptions / Tasks</TableHead>
                            <TableHead>Error</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {runs.map((run) => (
                            <TaskGenerationJobRunRow key={run.id} run={run} />
                        ))}
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    );
};

const TaskGenerationJobRunRow: FC<{ run: TaskGenerationJobRun }> = ({ run }) => {
    const onRowClick = useRowClick();
    return (
        <TableRow className="cursor-pointer" onClick={onRowClick(run.id)}>
            <TableCell className="font-mono text-xs">{run.id}</TableCell>
            <TableCell>
                <JobStatusBadge status={run.status} />
            </TableCell>
            <TableCell>{run.requestedAt ? new Date(run.requestedAt).toLocaleString() : '-'}</TableCell>
            <TableCell>{run.finishedAt ? new Date(run.finishedAt).toLocaleString() : '-'}</TableCell>
            <TableCell>
                {run.subscriptionCount !== undefined || run.taskCount !== undefined
                    ? `${run.subscriptionCount ?? '-'} / ${run.taskCount ?? '-'}`
                    : '-'}
            </TableCell>
            <TableCell>
                {run.errorMessage ? (
                    <span className="text-destructive text-sm max-w-md truncate block" title={run.errorMessage}>
                        {run.errorMessage}
                    </span>
                ) : (
                    <span className="text-muted-foreground">-</span>
                )}
            </TableCell>
        </TableRow>
    );
};
